'use server'

import { apiRequest, ApiResponse } from '../request'

export type ReportAutomationFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY'
export type ReportAutomationFormat = 'PDF' | 'XLSX' | 'CSV'
export type ReportAutomationPeriod =
    | 'TODAY'
    | 'YESTERDAY'
    | 'LAST_7_DAYS'
    | 'LAST_30_DAYS'
    | 'THIS_WEEK'
    | 'LAST_WEEK'
    | 'THIS_MONTH'
    | 'LAST_MONTH'
    | 'THIS_YEAR'

export interface ReportAutomation {
    id: number
    companyId: number
    name: string
    reportType: string
    frequency: ReportAutomationFrequency
    hourOfDay: number
    dayOfWeek: number | null
    dayOfMonth: number | null
    periodWindow: ReportAutomationPeriod
    format: ReportAutomationFormat
    recipients: string[]
    filtersJson: Record<string, any> | null
    active: boolean
    nextRunAt: string | null
    lastRunAt: string | null
    lastError: string | null
    lastSuccess: boolean | null
    lastFileUrl: string | null
    lastFileKey: string | null
    createdBy?: { id: number; name: string; email: string } | null
    createdAt: string
    updatedAt: string
}

export interface CreateReportAutomationData {
    name: string
    reportType: string
    frequency: ReportAutomationFrequency
    hourOfDay: number
    dayOfWeek?: number
    dayOfMonth?: number
    periodWindow: ReportAutomationPeriod
    format: ReportAutomationFormat
    recipients: string[]
    filtersJson?: Record<string, any>
    active?: boolean
}

export type UpdateReportAutomationData = Partial<CreateReportAutomationData>

const BASE = 'reports/automations'

export async function listReportAutomations(): Promise<ApiResponse<ReportAutomation[]>> {
    return apiRequest<ReportAutomation[]>(BASE, {})
}

export async function getReportAutomation(id: number): Promise<ApiResponse<ReportAutomation>> {
    return apiRequest<ReportAutomation>(`${BASE}/${id}`, {})
}

export async function createReportAutomation(
    data: CreateReportAutomationData,
): Promise<ApiResponse<ReportAutomation>> {
    return apiRequest<ReportAutomation>(BASE, { data }, 'post')
}

export async function updateReportAutomation(
    id: number,
    data: UpdateReportAutomationData,
): Promise<ApiResponse<ReportAutomation>> {
    return apiRequest<ReportAutomation>(`${BASE}/${id}`, { data }, 'patch')
}

export async function toggleReportAutomationActive(
    id: number,
): Promise<ApiResponse<ReportAutomation>> {
    return apiRequest<ReportAutomation>(`${BASE}/${id}/toggle-active`, {}, 'patch')
}

export async function runReportAutomationNow(
    id: number,
): Promise<ApiResponse<{ success: boolean; nextRunAt: string }>> {
    return apiRequest<{ success: boolean; nextRunAt: string }>(
        `${BASE}/${id}/run-now`,
        {},
        'post',
    )
}

export async function deleteReportAutomation(
    id: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(`${BASE}/${id}`, {}, 'delete')
}
